import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Pagination,
  PaginationItem,
} from "@mui/material";
import ReportFilter from "./ReportFilter";
import { fetchReportDetail } from "../../utils/Api";
import { useLocation } from "react-router-dom";
import moment from "moment";
import CustomBreadcrumbs from "../../components/CustomBreadcrumbs";
import GuestListDialogs from "./ReportDialogs/GuestListDialogs";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import { setMealType, setLocation } from "../../Redux/reportSlice";
import { employeeTableHeader } from "../../Data/data";

const ReportDetail = () => {
  const locationState = useLocation();
  const pathname = locationState.pathname.split("/");
  const payloadDate = pathname[3];

  // Redux state
  const dispatch = useDispatch();
  const { mealType, location } = useSelector((state) => state.report);

  // Local states
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [isOpened, setIsOpened] = useState(false);
  const [guestInfo, setGuestInfo] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const [filteredUsers, setFilteredUsers] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const rowsPerPage = 15;

  // On component mount, set initial Redux state based on location state
  useEffect(() => {
    if (locationState.state) {
      dispatch(setMealType(locationState.state.mealType || 1));
      dispatch(setLocation(locationState.state.location || 1));
    }
  }, [locationState.state, dispatch]);

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        const response = await fetchReportDetail(
          mealType,
          location,
          payloadDate
        );
        setData(response.data); // Adjust based on actual data structure
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch data");
        setLoading(false);
      }
      setIsLoading(false);
    };

    fetchData();
  }, [mealType, location, payloadDate]);

  //using the following state to filter the users based on the search
  useEffect(() => {
    const filteredData = data.filter(
      (item) =>
        item["employee.firstName"]
          .toLowerCase()
          .includes(searchUser.toLowerCase()) ||
        item["employee.lastName"]
          .toLowerCase()
          .includes(searchUser.toLowerCase())
    );
    setFilteredUsers(filteredData);
  }, [data, searchUser]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleOpenGuestInfo = (guests) => {
    setIsOpened(true);
    setGuestInfo(guests);
  };

  const handleExport = () => {
    const exportData = data?.map((item) => ({
      "Ordered By": `${item["employee.firstName"]} ${item["employee.lastName"]}`,
      Type: item["MealType.mealDisplayName"],
      Location: item["Location.displayName"],
      "Total Meal": item.mealCount,
      "Meal For":
        item.mealFor === "ctr-self"
          ? "Contract Employee"
          : item.mealFor?.charAt(0).toUpperCase() + item.mealFor?.slice(1),
      "Guest Info":
        item.guestInfo && item.guestInfo?.length
          ? item.guestInfo?.map((guest) => guest).join(", ")
          : "No Guests",
      "Ordered At": moment(item.createdAt).format("MMM D YY, h:mm a"),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report Details");
    XLSX.writeFile(workbook, `Report_Details_${payloadDate}.xlsx`);
  };

  const handleSearchUser = (e) => {
    setSearchUser(e.target.value);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <Typography variant="h6" color="error" align="center">
        {error}
      </Typography>
    );
  }

  const paginatedData = filteredUsers?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const breadcrumbItems = [
    { label: "Home", path: "/cms/dashboard" },
    { label: "Reports", path: "/cms/reports" },
  ];

  return (
    <>
      <CustomBreadcrumbs items={breadcrumbItems} currentLabel="Details" />
      <Box m={3}>
        <ReportFilter
          name={`Meal Details (${moment(payloadDate).format("MMM D, YY")})`}
          onClick={handleExport}
          searchUser={searchUser}
          handleSearchUser={handleSearchUser}
        />
        <TableContainer
          component={Paper}
          style={{ marginTop: "20px" }}
          sx={{
            border: "1px solid #E0E0E0",
            boxShadow: "none",
            overflow: "auto",
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#93CFEC33" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Ordered By</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Total Meal</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Meal For</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Guest Count</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Ordered At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedData?.length ? (
                paginatedData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {item["employee.firstName"]} {item["employee.lastName"]}
                    </TableCell>
                    <TableCell>{item["MealType.mealDisplayName"]}</TableCell>
                    <TableCell>{item["Location.displayName"]}</TableCell>
                    <TableCell>{item.mealCount}</TableCell>
                    <TableCell>
                      {item.mealFor === "ctr-self"
                        ? "Contract Employee"
                        : item.mealFor?.charAt(0).toUpperCase() +
                          item.mealFor?.slice(1)}
                    </TableCell>
                    <TableCell>
                      {item.guestInfo?.length ? (
                        <Typography
                          color="primary"
                          style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: "bold",
                            fontSize: "18px",
                          }}
                          onClick={() => handleOpenGuestInfo(item.guestInfo)}
                        >
                          {item.guestInfo?.length}
                        </Typography>
                      ) : (
                        <Typography variant="h5">_</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {moment(item.createdAt).format("MMM D YY, h:mm a")}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography>
            {filteredUsers.length > 0
              ? `${(page - 1) * rowsPerPage + 1} - ${Math.min(
                  page * rowsPerPage,
                  filteredUsers.length
                )} of ${filteredUsers.length}`
              : "0 results"}
          </Typography>

          <Pagination
            count={Math.ceil(filteredUsers?.length / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{
                  background: item.selected ? "#004D72  !important" : "",
                  color: item.selected ? "#fff !important" : "",
                }}
              />
            )}
          />
        </Box>
      </Box>
      <GuestListDialogs
        open={isOpened}
        onClose={() => setIsOpened(false)}
        guests={guestInfo}
      />
    </>
  );
};

export default ReportDetail;
